import React from 'react';
import {
    Box,
    Button,
    Container,
    Stack,
    Typography
} from '@mui/material';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useNavigate } from 'react-router-dom';
import siteText from '../content/siteText.json';
import SectionCard from './SectionCard';

const Home = () => {
    const navigate = useNavigate();
    const { home } = siteText;

    const imageCardSx = {
        borderRadius: 5,
        overflow: 'hidden',
        border: '1px solid rgba(0, 71, 110, 0.10)',
        boxShadow: '0 12px 30px rgba(0, 71, 110, 0.06)',
        backgroundColor: '#fff7fb',
        minHeight: { xs: 280, md: 360 }
    };

    const splitRowSx = {
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4,
        alignItems: 'stretch',
        mb: 4
    };

    const leftImageSx = {
        width: { xs: '100%', md: '42%' },
        flexShrink: 0
    };

    const rightTextSx = {
        width: { xs: '100%', md: '58%' },
        display: 'flex'
    };

    const leftTextSx = {
        width: { xs: '100%', md: '58%' },
        display: 'flex'
    };

    const rightImageSx = {
        width: { xs: '100%', md: '42%' },
        flexShrink: 0
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 4, md: 7 },
                width: 'min(92vw, 1600px)',
                mx: 'auto'
            }}
        >
            {/* Row 1 */}
            <Box sx={splitRowSx}>
                <Box sx={leftImageSx}>
                    <Box
                        sx={{
                            ...imageCardSx,
                            minHeight: { xs: 320, md: 420 },
                            height: '100%'
                        }}
                    >
                        <Box
                            component="img"
                            src="/img/outdoor_therapy_2.webp"
                            alt="Speech therapist working with a child"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={rightTextSx}>
                    <SectionCard
                        sx={{
                            width: '100%',
                            background: 'linear-gradient(180deg, #fff7fb 0%, #f7deed 100%)'
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                color: '#ff751f',
                                letterSpacing: 1.8,
                                fontWeight: 700
                            }}
                        >
                            {home.welcome}
                        </Typography>

                        <Typography
                            variant="h2"
                            sx={{
                                color: '#00476e',
                                fontWeight: 700,
                                lineHeight: 1.12,
                                mb: 2,
                                fontSize: { xs: '2rem', md: '3rem' }
                            }}
                        >
                            {home.heroTitle}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(0, 71, 110, 0.82)',
                                lineHeight: 1.85,
                                fontSize: '1.03rem',
                                mb: 3
                            }}
                        >
                            {home.heroBody}
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/services')}
                                sx={{
                                    backgroundColor: '#ff751f',
                                    color: '#ffffff',
                                    fontWeight: 700,
                                    px: 3,
                                    py: 1.25,
                                    borderRadius: 999,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#e86514',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {home.heroPrimaryCta}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => navigate('/contact')}
                                sx={{
                                    borderColor: '#00476e',
                                    color: '#00476e',
                                    fontWeight: 700,
                                    px: 3,
                                    py: 1.25,
                                    borderRadius: 999,
                                    '&:hover': {
                                        borderColor: '#00476e',
                                        backgroundColor: 'rgba(0, 71, 110, 0.04)'
                                    }
                                }}
                            >
                                {home.heroSecondaryCta}
                            </Button>
                        </Stack>
                    </SectionCard>
                </Box>
            </Box>

            {/* Row 2 */}
            <Box sx={splitRowSx}>
                <Box
                    sx={{
                        ...leftTextSx,
                        order: { xs: 2, md: 1 }
                    }}
                >
                    <SectionCard sx={{ width: '100%' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#00476e',
                                fontWeight: 700,
                                mb: 2
                            }}
                        >
                            {home.highlightsSectionTitle}
                        </Typography>

                        <Stack spacing={2}>
                            {home.highlights.map((item) => (
                                <Box
                                    key={item.title}
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: '#f7deed'
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ color: '#00476e', fontWeight: 700, mb: 0.75 }}
                                    >
                                        {item.title}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(0, 71, 110, 0.72)',
                                            lineHeight: 1.75
                                        }}
                                    >
                                        {item.body}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </SectionCard>
                </Box>

                <Box
                    sx={{
                        ...rightImageSx,
                        order: { xs: 1, md: 2 }
                    }}
                >
                    <Box
                        sx={{
                            ...imageCardSx,
                            height: '100%'
                        }}
                    >
                        <Box
                            component="img"
                            src="/img/therapist_and_child_6.webp"
                            alt="Cozy speech therapist office"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Row 3 */}
            <Box sx={splitRowSx}>
                <Box sx={leftImageSx}>
                    <Box
                        sx={{
                            ...imageCardSx,
                            height: '100%'
                        }}
                    >
                        <Box
                            component="img"
                            src="/img/therapist_and_woman.webp"
                            alt="Warm child-centered speech therapy session"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={rightTextSx}>
                    <SectionCard sx={{ width: '100%' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#00476e',
                                fontWeight: 700,
                                mb: 2
                            }}
                        >
                            {home.flexibleSchedulingTitle}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(0, 71, 110, 0.82)',
                                lineHeight: 1.85
                            }}
                        >
                            {home.flexibleSchedulingBody}
                        </Typography>
                    </SectionCard>
                </Box>
            </Box>

            {/* Testimonials */}
            <Box
                sx={{
                    mt: { xs: 6, md: 8 },
                    borderRadius: 5,
                    px: { xs: 3, md: 5 },
                    py: { xs: 4, md: 5 },
                    background: 'linear-gradient(180deg, #f7deed 0%, #f4d7e8 100%)',
                    border: '1px solid rgba(0, 71, 110, 0.08)',
                    boxShadow: '0 12px 30px rgba(0, 71, 110, 0.05)'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 4,
                        alignItems: 'stretch'
                    }}
                >
                    <Box sx={{ width: { xs: '100%', md: '33.333%' } }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: '#00476e',
                                fontWeight: 700,
                                mb: 1.5,
                                fontSize: { xs: '2rem', md: '2.6rem' }
                            }}
                        >
                            {home.testimonialsTitle}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(0, 71, 110, 0.82)',
                                lineHeight: 1.8,
                                mb: 3
                            }}
                        >
                            {home.testimonialsIntro}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: { xs: '100%', md: '66.667%' },
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 3
                        }}
                    >
                        {home.testimonials.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    flex: 1,
                                    borderRadius: 4,
                                    p: 3,
                                    backgroundColor: '#fff7fb',
                                    boxShadow: '0 10px 24px rgba(0, 71, 110, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#ff751f',
                                        fontSize: '3rem',
                                        lineHeight: 1,
                                        fontWeight: 700,
                                        mb: 1
                                    }}
                                >
                                    “
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'rgba(0, 71, 110, 0.82)',
                                        lineHeight: 1.85,
                                        flexGrow: 1,
                                        mb: 2
                                    }}
                                >
                                    {item.quote}
                                </Typography>

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: '#00476e',
                                        fontWeight: 700
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default Home;